import React, { useState } from 'react'
import './collaboration-request-form.css'

const SUBJECT_OPTIONS = [
  'Quiero solicitar una formación.',
  'Quiero solicitar una conferencia, charla o ponencia.',
  'Quiero solicitar un taller.',
  'Quiero solicitar una asesoría o acompañamiento.',
  'Quiero proponer la elaboración de un informe, guía, publicación u otro contenido.',
  'Quiero proponer una colaboración o proyecto conjunto.',
  'Tengo otra consulta.'
]

export default function CollaborationRequestForm({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    fullName: '',
    entity: '',
    position: '',
    email: '',
    phone: '',
    subject: '',
    details: '',
    deadline: '',
    privacyAccepted: false
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState('')
  const [submitError, setSubmitError] = useState('')

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError('')
    setSubmitMessage('')

    // Validate required fields
    if (!formData.fullName.trim()) {
      setSubmitError('El nombre y apellidos es requerido')
      return
    }

    if (!formData.email.trim() || !formData.email.includes('@')) {
      setSubmitError('El correo electrónico es requerido y debe ser válido')
      return
    }

    if (!formData.subject.trim()) {
      setSubmitError('Debes seleccionar un tema')
      return
    }

    if (!formData.details.trim()) {
      setSubmitError('Por favor, cuéntanos qué necesitas')
      return
    }

    if (!formData.privacyAccepted) {
      setSubmitError('Debes aceptar la Política de Privacidad')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/collaboration-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (!response.ok) {
        setSubmitError(data.error || 'Error al enviar el formulario')
        setIsSubmitting(false)
        return
      }

      setSubmitMessage('¡Gracias por tu mensaje! Nos pondremos en contacto contigo pronto.')
      setFormData({
        fullName: '',
        entity: '',
        position: '',
        email: '',
        phone: '',
        subject: '',
        details: '',
        deadline: '',
        privacyAccepted: false
      })

      setTimeout(() => {
        onClose()
        setSubmitMessage('')
      }, 2000)
    } catch (error) {
      setSubmitError('Error al enviar el formulario. Por favor, intenta de nuevo.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className='collaboration-request-form__overlay'>
      <div className='collaboration-request-form__modal'>
        <button className='collaboration-request-form__close' onClick={onClose} aria-label='Cerrar formulario'>
          ✕
        </button>

        <h2>Formulario de contacto para colaboraciones, servicios y consultas</h2>

        <form onSubmit={handleSubmit} className='collaboration-request-form__form'>
          {/* Nombre y apellidos */}
          <div className='collaboration-request-form__field'>
            <label htmlFor='fullName'>
              Nombre y apellidos <span className='collaboration-request-form__required'>*</span>
            </label>
            <input
              type='text'
              id='fullName'
              name='fullName'
              value={formData.fullName}
              onChange={handleChange}
              placeholder='Tu nombre completo'
            />
          </div>

          {/* Entidad, institución, empresa o colectivo */}
          <div className='collaboration-request-form__field'>
            <label htmlFor='entity'>Entidad, institución, empresa o colectivo</label>
            <input
              type='text'
              id='entity'
              name='entity'
              value={formData.entity}
              onChange={handleChange}
              placeholder='Nombre de la organización'
            />
          </div>

          {/* Cargo o función */}
          <div className='collaboration-request-form__field'>
            <label htmlFor='position'>Cargo o función</label>
            <input
              type='text'
              id='position'
              name='position'
              value={formData.position}
              onChange={handleChange}
              placeholder='Tu cargo o función'
            />
          </div>

          {/* Correo electrónico */}
          <div className='collaboration-request-form__field'>
            <label htmlFor='email'>
              Correo electrónico <span className='collaboration-request-form__required'>*</span>
            </label>
            <input
              type='email'
              id='email'
              name='email'
              value={formData.email}
              onChange={handleChange}
              placeholder='tu@correo.com'
            />
          </div>

          {/* Teléfono */}
          <div className='collaboration-request-form__field'>
            <label htmlFor='phone'>Teléfono</label>
            <input
              type='tel'
              id='phone'
              name='phone'
              value={formData.phone}
              onChange={handleChange}
              placeholder='+34 XXX XXX XXX'
            />
          </div>

          {/* Tema */}
          <div className='collaboration-request-form__field'>
            <label htmlFor='subject'>
              ¿Sobre qué quieres escribirnos? <span className='collaboration-request-form__required'>*</span>
            </label>
            <select
              id='subject'
              name='subject'
              value={formData.subject}
              onChange={handleChange}
            >
              <option value=''>Selecciona una opción</option>
              {SUBJECT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Detalles */}
          <div className='collaboration-request-form__field'>
            <label htmlFor='details'>
              Cuéntanos brevemente qué necesitas <span className='collaboration-request-form__required'>*</span>
            </label>
            <textarea
              id='details'
              name='details'
              value={formData.details}
              onChange={handleChange}
              placeholder='Explícanos qué tienes en mente o cuál es tu necesidad. No es necesario que entres en todos los detalles; podremos comentarlos más adelante.'
              rows='5'
            />
          </div>

          {/* Plazo */}
          <div className='collaboration-request-form__field'>
            <label htmlFor='deadline'>¿Hay alguna fecha o plazo que debamos tener en cuenta?</label>
            <input
              type='text'
              id='deadline'
              name='deadline'
              value={formData.deadline}
              onChange={handleChange}
              placeholder='Ej: 15 de agosto de 2026'
            />
          </div>

          {/* Política de privacidad */}
          <div className='collaboration-request-form__field collaboration-request-form__field--checkbox'>
            <input
              type='checkbox'
              id='privacyAccepted'
              name='privacyAccepted'
              checked={formData.privacyAccepted}
              onChange={handleChange}
            />
            <label htmlFor='privacyAccepted'>
              He leído y acepto la Política de Privacidad. <span className='collaboration-request-form__required'>*</span>
            </label>
          </div>

          {/* Mensajes de error y éxito */}
          {submitError && (
            <div className='collaboration-request-form__message collaboration-request-form__message--error'>
              {submitError}
            </div>
          )}
          {submitMessage && (
            <div className='collaboration-request-form__message collaboration-request-form__message--success'>
              {submitMessage}
            </div>
          )}

          {/* Botones */}
          <div className='collaboration-request-form__actions'>
            <button type='submit' disabled={isSubmitting} className='collaboration-request-form__submit'>
              {isSubmitting ? 'Enviando...' : 'Enviar'}
            </button>
            <button type='button' onClick={onClose} className='collaboration-request-form__cancel'>
              Cancelar
            </button>
          </div>
        </form>

        {/* Texto final */}
        <div className='collaboration-request-form__footer'>
          <p>
            <strong>Nos gusta conocer cada propuesta de manera cercana.</strong> Cuando recibamos tu mensaje, nos pondremos en contacto contigo en cuanto nos sea posible para conocer mejor tu propuesta y valorar juntas la mejor forma de colaborar.
          </p>
        </div>
      </div>
    </div>
  )
}
